import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getCacheEntry, setCacheEntry } from "../utilities/billerCache";
import {
    APIConstants,
    DeniedConstants,
    CommonAPIConstants,
    PatientTrackerConstants,
    formatDOSDate,
} from "../utilities/Constants";
import { exportExcel } from "../utilities/exportToExcel";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import "../css/denial-management.scss";

const DENIALS_CACHE_TTL = 2 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

// Payer filing window, measured from the date the claim was submitted.
// Flat constant for now — per-payer contract limits come later.
const FILING_DUE_DAYS = 120;
const DUE_SOON_DAYS = 21;


// Sentinel for the "All" rows-per-page option — kept numeric so the <select>
// value round-trips through Number() like every other choice.
const ALL_ROWS = -1;
const PAGE_SIZES = [10, 20, 30, 100, ALL_ROWS];

const AGING_BUCKETS = [
    { id: 1, range: "0-30 days", short: "0-30", min: 0, max: 30, hint: "Fresh - highest recovery rate" },
    { id: 2, range: "31-60 days", short: "31-60", min: 31, max: 60, hint: "Work before it ages further" },
    { id: 3, range: "61-90 days", short: "61-90", min: 61, max: 90, hint: "Escalate - recovery dropping" },
    { id: 4, range: "91-120 days", short: "91-120", min: 91, max: 120, hint: "Recovery window closing" },
    { id: 5, range: "120+ days", short: "120+", min: 121, max: Infinity, hint: "High write-off risk" },
];

const bucketShort = (id) => AGING_BUCKETS.find((b) => b.id === id)?.short ?? "-";

// A claim lands in exactly one of these; the predicates are mutually exclusive
// so the three non-"All" counts always sum to the "All" count.
const STATUS_TABS = [
    { key: "All", label: "All" },
    { key: "Needs Action", label: "Needs Action" },
    { key: "Resubmitted", label: "Resubmitted" },
    { key: "Paid", label: "✓ Paid", tone: "paid" },
];

const matchesTab = (item, tab) => {
    if (tab === "Needs Action") return item._needsAction;
    if (tab === "Resubmitted") return item.ClaimStatus === "Resubmitted" && !item._isPaid;
    if (tab === "Paid") return item._isPaid;
    return true; // "All"
};

const parseAmount = (val) => {
    if (val === null || val === undefined || val === "") return 0;
    const parsed = parseFloat(String(val).replace(/[^0-9.-]/g, ""));
    return Number.isNaN(parsed) ? 0 : parsed;
};

const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return String(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Convert yyyy-MM-dd (HTML date input) → MM/dd/yyyy (API format)
const toApiDate = (isoStr) => {
    const [y, m, d] = isoStr.split("-");
    return `${m}/${d}/${y}`;
};

const startOfDay = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d;
};

const daysSince = (value) => {
    const d = startOfDay(value);
    if (!d) return null;
    return Math.round((startOfDay(new Date()) - d) / DAY_MS);
};

const bucketIdForAge = (days) => {
    if (days === null) return null;
    const found = AGING_BUCKETS.find((b) => days >= b.min && days <= b.max);
    return found ? found.id : 5;
};

// Only open (Denied) claims carry a filing state — once reworked or paid the
// deadline no longer drives any action, so those rows read "—".
const dueStateFor = (daysLeft) => {
    if (daysLeft === null) return null;
    if (daysLeft < 0) return "overdue";
    if (daysLeft <= DUE_SOON_DAYS) return "duesoon";
    return "dueok";
};

const dueLabelFor = (daysLeft) => {
    if (daysLeft === null) return "-";
    if (daysLeft < 0) return `${Math.abs(daysLeft)}d past`;
    if (daysLeft === 0) return "Due today";
    return `${daysLeft}d left`;
};

const filingCellText = (item) => {
    if (!item._dueState) return "—";
    if (item._dueState === "overdue") return `⚠ Overdue ${Math.abs(item._daysToDue)}d`;
    if (item._dueState === "duesoon") return `⏰ Due in ${item._daysToDue}d`;
    return formatDOSDate(item._filingDue) || "—";
};

// One entry per sortable/searchable worklist column. `get` returns the text the
// column search matches against; `sort` overrides it when the natural sort order
// is not alphabetical (dates, numbers).
// `width` sizes each column; every column sorts, and all but CPT carry a search
// box in the filter row. The global search matches across all of them.
// Reason carries no width on purpose: the table is table-layout:fixed, so the
// one unsized column absorbs all remaining space and stays the widest.
const COLUMNS = [
    { key: "claim", label: "Claim #", width: 110, get: (i) => `CLM-${i.ClaimID ?? ""}` },
    { key: "patient", label: "Patient", width: 160, get: (i) => i._patient },
    { key: "dos", label: "DOS", width: 90, get: (i) => formatDOSDate(i.DOS) || "", sort: (i) => startOfDay(i.DOS)?.getTime() ?? 0 },
    { key: "age", label: "Age", width: 130, get: (i) => (i._ageDays === null ? "" : String(i._ageDays)), sort: (i) => i._ageDays ?? -1 },
    {
        key: "filingdue", label: "Filing Due", width: 130,
        get: (i) => filingCellText(i),
        // Rows with no deadline sort last in either direction.
        sort: (i) => (i._daysToDue === null || !i._dueState ? Number.MAX_SAFE_INTEGER : i._daysToDue),
    },
    { key: "payer", label: "Payer", width: 120, get: (i) => i._payer },
    { key: "cpt", label: "CPT", width: 70, search: false, get: (i) => i.CPT || "" },
    { key: "code", label: "Code", width: 80, get: (i) => i.DeniedCode || "" },
    { key: "reason", label: "Reason", get: (i) => i.ErrorDetails || "" },
    { key: "submitter", label: "Submitted By", width: 110, get: (i) => i._submittedBy },
    { key: "status", label: "Status", width: 110, get: (i) => statusLabel(i) },
];

const emptyColumnSearch = () => Object.fromEntries(COLUMNS.map((c) => [c.key, ""]));

const parsePayload = (raw) => {
    let parsed = raw;
    if (parsed && Object.prototype.hasOwnProperty.call(parsed, "d")) parsed = parsed.d;
    while (typeof parsed === "string") {
        try {
            parsed = JSON.parse(parsed);
        } catch {
            break;
        }
    }
    return parsed;
};

const normalizeDeniedCode = (raw) => String(raw || "").trim().toUpperCase();

const isDeniedReasonCode = (value) => {
    const v = normalizeDeniedCode(value);
    if (!v) return false;
    // Avoid numeric status-like values such as "7"
    if (/^\d+$/.test(v)) return false;
    return /^(CO|PR|OA|PI)-?\d{1,4}$/.test(v) || /^N\d{2,4}$/.test(v) || /^[A-Z]{1,3}\d{2,4}$/.test(v);
};

const extractDeniedCode = (claim) => {
    const directCandidates = [
        claim?.CPTLineAdjustCode,
        claim?.DeniedCode,
        claim?.ReasonCode,
        claim?.AdjustCode,
        claim?.AdjCode,
        claim?.ResubmitCode,
        claim?.CARC,
        claim?.CARCCode,
        claim?.ClaimAdjustCode,
        claim?.ClaimAdjustmentCode,
    ];

    for (const candidate of directCandidates) {
        if (isDeniedReasonCode(candidate)) return normalizeDeniedCode(candidate);
    }

    const text = String(
        claim?.ErrorDetails ||
        claim?.DenialReason ||
        claim?.Reason ||
        claim?.ClaimNotes ||
        ""
    );

    const match = text.match(/\b((?:CO|PR|OA|PI)-?\d{1,4}|N\d{2,4}|[A-Z]{1,3}\d{2,4})\b/i);
    return match ? normalizeDeniedCode(match[1]) : "Unknown";
};

const statusPillClass = (item) => {
    if (item?._isPaid) return "dm-pill dm-pill--paid";
    if (item?.ClaimStatus === "Resubmitted") return "dm-pill dm-pill--resub";
    return "dm-pill dm-pill--denied";
};

const statusLabel = (item) => (item?._isPaid ? "✓ Paid" : item?.ClaimStatus || "Denied");

// The denied endpoint carries no dedicated "payment posted" flag today, so accept
// any of the shapes the billing services use for 835 / posting reconciliation.
// If none are present this stays false and the ✓ Paid tab reads zero.
const isPaymentPosted = (row) => {
    const flag = row?.isPaymentPosted ?? row?.IsPaymentPosted ?? row?.isPaymentReceived ?? row?.IsPaymentReceived;
    const s = String(flag).toLowerCase();
    if (flag === true || flag === 1 || s === "true" || s === "1" || s === "y") return true;
    return Boolean(row?.PaymentPostedDate || row?.PaymentChequeDate || row?.PaymentChqueNo || row?.PaymentChequeNo);
};

// The filter-type control. A native <select> renders its popup through the OS,
// which no stylesheet can reach — this is a listbox so the open menu matches the
// rest of the page. Keeps native keyboard behaviour: arrows move, Enter picks,
// Escape closes, Tab leaves.
function DmSelect({ value, options, onChange, ariaLabel }) {
    const [open, setOpen] = useState(false);
    const [activeIdx, setActiveIdx] = useState(0);
    const rootRef = useRef(null);
    const current = options.find((o) => o.value === value) || options[0];

    useEffect(() => {
        if (!open) return undefined;
        setActiveIdx(Math.max(0, options.findIndex((o) => o.value === value)));
        const onDocDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocDown);
        return () => document.removeEventListener("mousedown", onDocDown);
    }, [open, options, value]);

    const choose = (opt) => {
        onChange(opt.value);
        setOpen(false);
    };

    const onKeyDown = (e) => {
        if (e.key === "Escape") { setOpen(false); return; }
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            if (!open) { setOpen(true); return; }
            setActiveIdx((i) => {
                const n = options.length;
                return e.key === "ArrowDown" ? (i + 1) % n : (i - 1 + n) % n;
            });
            return;
        }
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (open) choose(options[activeIdx]);
            else setOpen(true);
        }
    };

    return (
        <div className="dm-select" ref={rootRef}>
            <button
                type="button"
                className={`dm-select__btn ${open ? "dm-select__btn--open" : ""}`}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={ariaLabel}
                onClick={() => setOpen((v) => !v)}
                onKeyDown={onKeyDown}
            >
                <span>{current?.label}</span>
                <svg className="dm-select__chev" viewBox="0 0 12 12" aria-hidden="true">
                    <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>

            {open && (
                <ul className="dm-select__menu" role="listbox" aria-label={ariaLabel}>
                    {options.map((opt, i) => (
                        <li
                            key={opt.value}
                            role="option"
                            aria-selected={opt.value === value}
                            className={`dm-select__opt ${opt.value === value ? "dm-select__opt--on" : ""} ${i === activeIdx ? "dm-select__opt--active" : ""}`}
                            onMouseEnter={() => setActiveIdx(i)}
                            onMouseDown={(e) => { e.preventDefault(); choose(opt); }}
                        >
                            {opt.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

const FILTER_TYPE_OPTIONS = [{ value: "DOS", label: "DOS" }];

const KPI_TONES = {
    red: "#dc2626",
    amber: "#d97706",
    purple: "#7c3aed",
    deep: "#991b1b",
    green: "#15803d",
    tealDark: "#0e6f5c",
};

export default function DeniedDashboardPreview({ handleLogout }) {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);
    const closeSidebar = () => setIsSidebarOpen(false);

    const reduxUser = useSelector((s) => s.user.user);
    const reduxToken = useSelector((s) => s.user.token);
    const userData = reduxUser || (() => {
        try {
            return JSON.parse(localStorage.getItem("userData"));
        } catch {
            return null;
        }
    })();
    const sAuthToken = reduxToken || localStorage.getItem("sAuthToken");
    const practiceID = userData?.practiceID;

    const [claims, setClaims] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // View + queue controls
    const [view, setView] = useState("queue");
    const [searchText, setSearchText] = useState("");
    const [statusChip, setStatusChip] = useState("All");
    const [bucketFilter, setBucketFilter] = useState(null);
    const [columnSearch, setColumnSearch] = useState(emptyColumnSearch);
    // Nearest filing deadline first — the queue opens on what expires soonest.
    const [sortKey, setSortKey] = useState("filingdue");
    const [sortDir, setSortDir] = useState("asc");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [selected, setSelected] = useState(() => new Set());
    const [alertDismissed, setAlertDismissed] = useState(false);
    const [dueSoonOnly, setDueSoonOnly] = useState(false);

    // Daily Activity view
    const [dailyDate, setDailyDate] = useState(() => new Date().toISOString().split("T")[0]);
    const [dailyRange, setDailyRange] = useState("today");
    const [audits, setAudits] = useState([]);
    const [claimEvents, setClaimEvents] = useState([]);
    const [dailyLoading, setDailyLoading] = useState(false);
    const [dailyError, setDailyError] = useState("");

    // Detail drawer
    const [drawerClaim, setDrawerClaim] = useState(null);
    const [resubCPT, setResubCPT] = useState("");
    const [resubICD, setResubICD] = useState("");
    const [resubDOS, setResubDOS] = useState("");
    const [resubNotes, setResubNotes] = useState("");
    const [resubLoading, setResubLoading] = useState(false);
    const [resubMsg, setResubMsg] = useState(null);

    // Date range controls
    const _today = new Date();
    const todayIso = _today.toISOString().split("T")[0];
    const minIso = (() => { const d = new Date(_today); d.setFullYear(_today.getFullYear() - 2); return d.toISOString().split("T")[0]; })();
    // Default: 1 year back (matches the initial API fetch)
    const defaultFromIso = (() => { const d = new Date(_today); d.setFullYear(_today.getFullYear() - 1); return d.toISOString().split("T")[0]; })();

    const [dateTab, setDateTab] = useState("custom");
    const [fromDateInput, setFromDateInput] = useState(defaultFromIso);
    const [toDateInput, setToDateInput] = useState(todayIso);
    const [filterType, setFilterType] = useState("DOS");
    const [showPracticeClaims, setShowPracticeClaims] = useState(false);

    const fetchDeniedClaims = useCallback(async ({ silent = false, from, to, fType, showPractice } = {}) => {
        if (!silent) {
            setLoading(true);
            setError("");
        }
        const scopeToPractice = showPractice ?? false;
        const cacheKey = `biller-denials:${practiceID}:${scopeToPractice ? "practice" : "own"}`;
        try {
            // GetDeniedClaimsDetails wants sInsorPatFilter EMPTY (no space) and
            // reads sFilterText as the practice ID. Anything else — a mode string,
            // or " " with a space — comes back with zero rows.
            const objInput = {
                sFromDate: from,
                sToDate: to,
                sFilterType: fType ?? "DOS",
                sFilterText: scopeToPractice ? "" : String(practiceID),
                sInsorPatFilter: "",
                iPracticeID: String(practiceID),
            };
            const url = `${APIConstants.BASE_URL_BILLING}${DeniedConstants.GetDeniedClaims}?sInputJSON=${encodeURIComponent(JSON.stringify(objInput))}`;
            const res = await fetch(url, {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${sAuthToken}`,
                },
            });
            if (res.status !== 200) throw new Error(`Server returned ${res.status}`);
            const parsed = parsePayload(await res.json()) || {};
            const data = parsed?.Data?.objClaims || parsed?.Data?.[0] || parsed?.Data || [];
            if (!Array.isArray(data)) throw new Error("Unexpected response format");

            setClaims(data);
            setCacheEntry(cacheKey, data);
        } catch (err) {
            if (!silent) setError(err.message || "Failed to load denial data.");
        } finally {
            if (!silent) setLoading(false);
        }
    }, [practiceID, sAuthToken]);

    useEffect(() => {
        document.title = "Denial Management";
        if (!practiceID) {
            setLoading(false);
            return;
        }
        // Must match the key fetchDeniedClaims writes for the default (own) scope.
        const cacheKey = `biller-denials:${practiceID}:own`;
        const cachedClaims = getCacheEntry(cacheKey, DENIALS_CACHE_TTL);
        const hasCachedClaims = Array.isArray(cachedClaims);

        if (hasCachedClaims) {
            setClaims(cachedClaims);
            setLoading(false);
        }

        // Use the same dates shown in the UI inputs
        fetchDeniedClaims({ silent: hasCachedClaims, from: toApiDate(defaultFromIso), to: toApiDate(todayIso) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceID, sAuthToken]);

    const groupedClaims = useMemo(() => {
        const map = new Map();
        claims.forEach((row, idx) => {
            const claimId = String(row?.ClaimID || row?.ClaimId || "").trim();
            const key = claimId || `${row?.PatientID || "p"}-${row?.DOS || "d"}-${row?.CPT || "c"}-${idx}`;
            const code = extractDeniedCode(row);
            const cpt = String(row?.CPT || row?.procedure || row?.CPTCode || "").trim();
            const reason = String(row?.ErrorDetails || row?.DenialReason || row?.Reason || "").trim();
            const status = String(row?.ClaimStatus || "Denied").trim();

            if (!map.has(key)) {
                map.set(key, {
                    ...row,
                    _key: key,
                    _base: row,
                    _totalBilled: parseAmount(row?.TotalCharge),
                    _codeSet: new Set(code !== "Unknown" ? [code] : []),
                    _cptSet: new Set(cpt ? [cpt] : []),
                    _reasonSet: new Set(reason ? [reason] : []),
                    _statusSet: new Set(status ? [status] : ["Denied"]),
                });
                return;
            }

            const grouped = map.get(key);
            grouped._totalBilled += parseAmount(row?.TotalCharge);
            grouped._statusSet.add(status || "Denied");
            if (cpt) grouped._cptSet.add(cpt);
            if (code !== "Unknown") grouped._codeSet.add(code);
            if (reason) grouped._reasonSet.add(reason);
            if (!grouped._base?.CPTRowID && row?.CPTRowID) grouped._base = row;
        });

        return Array.from(map.values()).map((grouped) => {
            const cptList = Array.from(grouped._cptSet);
            const codeList = Array.from(grouped._codeSet);
            const reasonList = Array.from(grouped._reasonSet);

            const claimStatus = grouped._statusSet.has("Resubmitted") ? "Resubmitted" : "Denied";
            const paid = claimStatus === "Resubmitted" && isPaymentPosted(grouped);

            // Aging is always measured from date of service.
            const ageDays = daysSince(grouped.DOS);

            // Filing deadline always runs from the submit date, independent of
            // the age basis. Only open claims carry a due state.
            const submitDay = startOfDay(grouped.ClaimSubmitDate);
            const filingDue = submitDay ? new Date(submitDay.getTime() + FILING_DUE_DAYS * DAY_MS) : null;
            const daysToDue = filingDue
                ? Math.round((filingDue - startOfDay(new Date())) / DAY_MS)
                : null;
            const dueState = claimStatus === "Denied" ? dueStateFor(daysToDue) : null;

            return {
                ...grouped,
                ClaimStatus: claimStatus,
                CPTList: cptList,
                CPT: cptList.length <= 1 ? (cptList[0] || grouped.CPT || "-") : `${cptList[0]} +${cptList.length - 1}`,
                DeniedCodeList: codeList,
                DeniedCode: codeList.length <= 1 ? (codeList[0] || "Unknown") : `${codeList[0]} +${codeList.length - 1}`,
                ErrorDetails: reasonList.join(" | "),
                TotalCharge: Number(grouped._totalBilled.toFixed(2)),
                _payer: String(grouped.InsuranceName || grouped.InsuranceID || "Unknown").trim() || "Unknown",
                // GetDeniedClaimsDetails calls this SubmittedUserName; ClaimStatusData
                // calls the same person SubmitterName / SubmittedBy. Accept any of them.
                _submittedBy: String(
                    grouped.SubmittedUserName || grouped.SubmitterName || grouped.SubmittedBy || ""
                ).trim(),
                _patient: `${grouped.LastName || ""} ${grouped.FirstName || ""}`.trim() || "Unknown",
                _ageDays: ageDays,
                _bucket: bucketIdForAge(ageDays),
                _filingDue: filingDue,
                _daysToDue: daysToDue,
                _dueState: dueState,
                _dueSoonOrOverdue: dueState === "duesoon" || dueState === "overdue",
                _isPaid: paid,
                _needsAction: claimStatus !== "Resubmitted",
            };
        });
    }, [claims]);

    const needsActionCount = groupedClaims.filter((c) => c._needsAction).length;
    const paidCount = groupedClaims.filter((c) => c._isPaid).length;
    const overdueCount = groupedClaims.filter((c) => c._dueState === "overdue").length;
    const dueSoonCount = groupedClaims.filter((c) => c._dueState === "duesoon").length;

    const avgAgeDays = useMemo(() => {
        const ages = groupedClaims.map((c) => c._ageDays).filter((d) => d !== null);
        if (!ages.length) return 0;
        return Math.round(ages.reduce((s, d) => s + d, 0) / ages.length);
    }, [groupedClaims]);

    const bucketCounts = useMemo(() => {
        const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        groupedClaims.forEach((c) => {
            if (c._bucket) counts[c._bucket] += 1;
        });
        return counts;
    }, [groupedClaims]);

    const reasonBreakdown = useMemo(() => {
        const stats = new Map();
        groupedClaims.forEach((claim) => {
            const codes = claim.DeniedCodeList?.length ? claim.DeniedCodeList : [extractDeniedCode(claim)];
            Array.from(new Set(codes)).forEach((code) => {
                const entry = stats.get(code) || { code, count: 0, why: "" };
                entry.count += 1;
                if (!entry.why && claim.ErrorDetails) entry.why = claim.ErrorDetails.split(" | ")[0];
                stats.set(code, entry);
            });
        });
        const total = groupedClaims.length || 1;
        return Array.from(stats.values())
            .sort((a, b) => b.count - a.count)
            .slice(0, 6)
            .map((entry) => ({ ...entry, percent: Math.round((entry.count / total) * 100) }));
    }, [groupedClaims]);

    const payerBreakdown = useMemo(() => {
        const counts = new Map();
        groupedClaims.forEach((c) => counts.set(c._payer, (counts.get(c._payer) || 0) + 1));
        const total = groupedClaims.length || 1;
        return Array.from(counts.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 6)
            .map(([payer, count]) => ({ payer, count, percent: Math.round((count / total) * 100) }));
    }, [groupedClaims]);

    const payerMatrix = useMemo(() => {
        const rows = new Map();
        groupedClaims.forEach((c) => {
            const row = rows.get(c._payer) || { payer: c._payer, cells: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }, total: 0 };
            if (c._bucket) row.cells[c._bucket] += 1;
            row.total += 1;
            rows.set(c._payer, row);
        });
        const list = Array.from(rows.values()).sort((a, b) => b.total - a.total).slice(0, 8);
        const totals = { payer: "All payers", cells: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }, total: 0 };
        list.forEach((row) => {
            AGING_BUCKETS.forEach((b) => { totals.cells[b.id] += row.cells[b.id]; });
            totals.total += row.total;
        });
        return { list, totals };
    }, [groupedClaims]);

    // Everything except the tab. The tab badges count off this list, so each badge
    // answers "how many would I see if I clicked here" under the current search,
    // column filters and aging bucket.
    const preTabFiltered = useMemo(() => {
        const q = searchText.toLowerCase().trim();
        return groupedClaims.filter((item) => {
            // Global search spans every column the grid renders.
            const matchesSearch =
                !q || COLUMNS.some((col) => String(col.get(item)).toLowerCase().includes(q));

            const matchesBucket = !bucketFilter || item._bucket === bucketFilter;

            const matchesDueSoon = !dueSoonOnly || item._dueSoonOrOverdue;

            const matchesColumns = COLUMNS.every((col) => {
                const term = (columnSearch[col.key] || "").trim().toLowerCase();
                return !term || String(col.get(item)).toLowerCase().includes(term);
            });

            return matchesSearch && matchesBucket && matchesDueSoon && matchesColumns;
        });
    }, [groupedClaims, bucketFilter, dueSoonOnly, searchText, columnSearch]);

    const tabCounts = useMemo(() => {
        const counts = {};
        STATUS_TABS.forEach((t) => { counts[t.key] = 0; });
        preTabFiltered.forEach((item) => {
            STATUS_TABS.forEach((t) => { if (matchesTab(item, t.key)) counts[t.key] += 1; });
        });
        return counts;
    }, [preTabFiltered]);

    const filtered = useMemo(
        () => preTabFiltered.filter((item) => matchesTab(item, statusChip)),
        [preTabFiltered, statusChip]
    );

    const sorted = useMemo(() => {
        const dir = sortDir === "asc" ? 1 : -1;
        const col = COLUMNS.find((c) => c.key === sortKey);
        const value = (item) => {
            if (!col) return item._ageDays ?? -1;
            return col.sort ? col.sort(item) : String(col.get(item)).toLowerCase();
        };
        return [...filtered].sort((a, b) => {
            const av = value(a);
            const bv = value(b);
            if (av < bv) return -1 * dir;
            if (av > bv) return 1 * dir;
            return 0;
        });
    }, [filtered, sortKey, sortDir]);

    // "All" collapses to a single page holding everything.
    const effectivePageSize = pageSize === ALL_ROWS ? Math.max(sorted.length, 1) : pageSize;
    const totalPages = Math.max(1, Math.ceil(sorted.length / effectivePageSize));
    const currentPage = Math.min(page, totalPages);
    const paged = useMemo(
        () => sorted.slice((currentPage - 1) * effectivePageSize, currentPage * effectivePageSize),
        [sorted, currentPage, effectivePageSize]
    );

    useEffect(() => {
        setPage(1);
    }, [statusChip, bucketFilter, dueSoonOnly, searchText, columnSearch, pageSize, sortKey, sortDir]);

    // Sliding window of 5 page buttons, clamped to the ends so the control keeps
    // a constant width whether there are 3 pages or 300.
    const pageWindow = useMemo(() => {
        const span = Math.min(5, totalPages);
        let start = Math.max(1, currentPage - Math.floor(span / 2));
        start = Math.min(start, totalPages - span + 1);
        return Array.from({ length: span }, (_, i) => start + i);
    }, [currentPage, totalPages]);

    const handleColumnSearch = (key, value) =>
        setColumnSearch((prev) => ({ ...prev, [key]: value }));

    // Drives the removable chips row; stays in sync with the header inputs
    // because both read the same columnSearch state.
    const activeFilters = COLUMNS
        .filter((c) => (columnSearch[c.key] || "").trim() !== "")
        .map((c) => ({ key: c.key, label: c.label, value: columnSearch[c.key] }));

    const allFilteredSelected = sorted.length > 0 && sorted.every((i) => selected.has(i._key));

    const toggleSelectAllFiltered = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allFilteredSelected) sorted.forEach((i) => next.delete(i._key));
            else sorted.forEach((i) => next.add(i._key));
            return next;
        });
    };

    const handleTabChange = (tab) => {
        setDateTab(tab);
        const today = new Date();
        const toIso = today.toISOString().split("T")[0];
        const from = new Date(today);
        if (tab === "week") {
            from.setDate(today.getDate() - 7);
        } else if (tab === "15days") {
            from.setDate(today.getDate() - 15);
        } else if (tab === "month") {
            from.setMonth(today.getMonth() - 1);
        } else {
            return; // "custom" — wait for Search click
        }
        const fromIso = from.toISOString().split("T")[0];
        setFromDateInput(fromIso);
        setToDateInput(toIso);
        fetchDeniedClaims({ from: toApiDate(fromIso), to: toApiDate(toIso), fType: filterType, showPractice: showPracticeClaims });
    };

    const handleApplyDates = () => {
        setDateTab("custom");
        fetchDeniedClaims({ from: toApiDate(fromDateInput), to: toApiDate(toDateInput), fType: filterType, showPractice: showPracticeClaims });
    };

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir("asc");
        }
    };

    // ── Daily Activity ───────────────────────────────────────────────────
    // Window the selected date resolves to. "This Week" runs Monday→selected day.
    const dailyWindow = useMemo(() => {
        const end = startOfDay(dailyDate) || startOfDay(new Date());
        if (dailyRange === "week") {
            const start = new Date(end);
            const dow = (start.getDay() + 6) % 7; // Monday = 0
            start.setDate(start.getDate() - dow);
            return { from: start, to: end };
        }
        if (dailyRange === "yesterday") {
            const d = new Date(end.getTime() - DAY_MS);
            return { from: d, to: d };
        }
        return { from: end, to: end };
    }, [dailyDate, dailyRange]);

    const fetchDailyActivity = useCallback(async () => {
        if (!practiceID) return;
        setDailyLoading(true);
        setDailyError("");
        const from = toApiDate(formatDate(dailyWindow.from));
        const to = toApiDate(formatDate(dailyWindow.to));

        const auditInput = {
            PracticeID: practiceID,
            DataID: 0,
            Module: "Billing",
            SubModule: "",
            sFromDate: from,
            sToDate: to,
            PatientID: "",
            UserID: "",
        };
        // DataID 0 asks for every claim in the window rather than one claim.
        const eventInput = {
            PracticeID: practiceID,
            ModuleName: "Billing",
            dtFromDate: from,
            dtToDate: to,
            DataID: 0,
            UserID: 0,
            DateType: "DOS",
        };

        const get = async (url) => {
            const res = await fetch(url, {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${sAuthToken}`,
                },
            });
            if (res.status !== 200) throw new Error(`Server returned ${res.status}`);
            const parsed = parsePayload(await res.json());
            const data = parsed?.Data ?? parsed;
            return Array.isArray(data) ? data : [];
        };

        try {
            const [auditRows, eventRows] = await Promise.all([
                get(`${APIConstants.BASE_AUDIT_URL}${PatientTrackerConstants.GetUserAudits}?sInputJSON=${encodeURIComponent(JSON.stringify(auditInput))}`)
                    .catch(() => []),
                get(`${APIConstants.COMMON_BASE_URL}${CommonAPIConstants.GetClaimAllEvents}?sJSONInput=${encodeURIComponent(JSON.stringify(eventInput))}`)
                    .catch(() => []),
            ]);
            setAudits(auditRows);
            setClaimEvents(eventRows);
            if (!auditRows.length && !eventRows.length) {
                setDailyError("No activity returned for this date.");
            }
        } catch (err) {
            setDailyError(err.message || "Failed to load activity.");
        } finally {
            setDailyLoading(false);
        }
    }, [practiceID, sAuthToken, dailyWindow]);

    useEffect(() => {
        if (view === "daily") fetchDailyActivity();
    }, [view, fetchDailyActivity]);

    // Filters the whole page, not just the queue — the management panels read
    // the same bucket-scoped list. Clicking the selected bucket clears it.
    const handleBucketClick = (bucketId) => {
        setBucketFilter((prev) => (prev === bucketId ? null : bucketId));
    };

    const toggleSelect = (key) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    // Exports the current selection, or everything matching the filters when
    // nothing is ticked — so the button is never a no-op.
    const exportQueue = () => {
        const source = selected.size > 0 ? sorted.filter((i) => selected.has(i._key)) : sorted;
        const rows = source
            .map((item, index) => ({
                "#": index + 1,
                "Claim ID": item.ClaimID || "",
                Patient: item._patient,
                DOS: formatDOSDate(item.DOS) || "",
                "Age (days)": item._ageDays ?? "",
                Payer: item._payer,
                CPT: item.CPT || "",
                "Reason Code": item.DeniedCode || "",
                Reason: item.ErrorDetails || "",
                Billed: parseAmount(item.TotalCharge).toFixed(2),
                "Submitted By": item._submittedBy,
                "Filing Due": item._filingDue ? formatDOSDate(item._filingDue) : "",
                "Days To Deadline": item._daysToDue ?? "",
                Status: item.ClaimStatus || "",
            }));
        if (rows.length) exportExcel(rows, "DenialWorklist", "Denials");
    };

    const openDrawer = (item) => {
        const base = item?._base || item;
        setDrawerClaim(item);
        setResubCPT(base.CPT || item.CPTList?.[0] || "");
        setResubICD(base.ICDCode || base.ICD10 || "");
        setResubDOS(formatDate(base.DOS) || "");
        setResubNotes("");
        setResubMsg(null);
    };

    const closeDrawer = () => {
        setDrawerClaim(null);
        setResubLoading(false);
    };

    const handleResubmitSubmit = async () => {
        const resubClaim = drawerClaim?._base || drawerClaim;
        if (!resubClaim || resubLoading) return;
        setResubLoading(true);
        setResubMsg(null);
        try {
            const inputStruct = {
                IsSaveforApproval: 1,
                ClearingHouse: 0,
                PracticeID: practiceID,
                IsFromDenial: 1,
                UserID: userData?.userID || "",
                SelectedClaims: [
                    {
                        ClaimID: resubClaim.ClaimID,
                        PatientID: String(resubClaim.PatientID || ""),
                        DOS: resubDOS || formatDate(resubClaim.DOS),
                        Procedure: resubCPT || resubClaim.CPT || "",
                        ICDCode1: resubICD || resubClaim.ICDCode || "",
                        TotalCharge: resubClaim.TotalCharge || "0",
                        PostingDate: "",
                        PlaceofService: "",
                        CPTRowID: resubClaim.CPTRowID || "",
                        SchedulingProvider: "",
                        PrimaryIns: "",
                        Claim32a: "",
                        Claim33a: "",
                        Claim33b: "",
                        ServiceLocationCode: "",
                        RenderingProvider: "",
                        PolicyNumber: "",
                        Mod1: "",
                        Mod2: "",
                        Mod3: "",
                        ICDCode2: "",
                        ICDCode3: "",
                        ICDCode4: "",
                        Unit: resubClaim.Unit || "",
                        UnitCharge: resubClaim.UnitCharge || "",
                        ExternalClaimID: "",
                        Box17Indicator: "",
                        SupervisingProvider: "",
                        PriorSec: "",
                    },
                ],
            };

            const body = JSON.stringify(JSON.stringify({ objClaim: inputStruct }));
            const res = await fetch("/api/billing/Billing/UpdateClaims", {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${sAuthToken}`,
                },
                body,
            });
            if (!res.ok) throw new Error(`Server returned ${res.status}`);

            const stored = JSON.parse(localStorage.getItem("resubmittedClaims") || "[]");
            if (!stored.some((claim) => String(claim.ClaimID) === String(resubClaim.ClaimID))) {
                stored.push({
                    ...resubClaim,
                    ClaimStatus: "Resubmitted",
                    resubCPT,
                    resubICD,
                    resubDOS,
                    resubNotes,
                    resubmittedAt: new Date().toISOString(),
                });
                localStorage.setItem("resubmittedClaims", JSON.stringify(stored));
            }

            setClaims((prev) =>
                prev.map((claim) =>
                    String(claim.ClaimID) === String(resubClaim.ClaimID)
                        ? { ...claim, ClaimStatus: "Resubmitted" }
                        : claim
                )
            );
            setResubMsg({ text: "Claim resubmitted successfully.", ok: true });
            setTimeout(closeDrawer, 1500);
        } catch (err) {
            setResubMsg({ text: err.message || "Resubmit failed.", ok: false });
        } finally {
            setResubLoading(false);
        }
    };

    // EncounterV2 resolves the patient from the claim ID alone — it queries
    // ClaimStatusData with sInsorPatFilter "claimID" and pre-selects the patient,
    // so no lookup is needed on arrival. Without an ID it would open a blank form
    // with nothing to resolve, so don't navigate at all.
    const openEncounter = (item) => {
        if (!item?.ClaimID) return;
        window.open(`/#/new-encounter-v2/${item.ClaimID}`, "_blank");
    };

    // Normalises the two feeds into one shape. Field names vary between the audit
    // and event services, so every accessor probes the plausible aliases.
    const activity = useMemo(() => {
        const pick = (r, ...keys) => {
            for (const k of keys) if (r?.[k] !== undefined && r[k] !== null && r[k] !== "") return r[k];
            return "";
        };
        const rows = [
            ...audits.map((r) => ({
                kind: "audit",
                biller: String(pick(r, "UserName", "UserFullName", "CreatedBy", "ModifiedBy")),
                action: String(pick(r, "ActionName", "Action", "AuditAction", "Description", "Event")).toLowerCase(),
                claimId: String(pick(r, "ClaimID", "DataID", "ClaimId")),
                when: pick(r, "AuditDate", "CreatedDate", "ModifiedDate", "EventDate"),
                detail: String(pick(r, "Description", "Remarks", "Notes")),
            })),
            ...claimEvents.map((r) => ({
                kind: "event",
                biller: String(pick(r, "UserName", "CreatedBy", "SubmitterName")),
                action: String(pick(r, "EventName", "Event", "StatusName", "Description")).toLowerCase(),
                claimId: String(pick(r, "ClaimID", "DataID", "ClaimId")),
                when: pick(r, "EventDate", "CreatedDate", "TransDate"),
                detail: String(pick(r, "Description", "Remarks", "StatusName")),
            })),
        ];
        // Only denial-workflow actions belong in this feed.
        const DENIAL_WORDS = /(deni|resubmit|rework|correct|appeal|remit|835|payment|post|accept|reject|clearing)/i;
        return rows
            .filter((r) => r.claimId && (DENIAL_WORDS.test(r.action) || DENIAL_WORDS.test(r.detail)))
            .sort((a, b) => new Date(b.when) - new Date(a.when));
    }, [audits, claimEvents]);

    const isPaymentEvent = (r) => /(remit|835|payment|paid)/i.test(`${r.action} ${r.detail}`);

    // Per-biller rollup for the "Worked by Biller" table.
    const billerRollup = useMemo(() => {
        const map = new Map();
        activity.forEach((r) => {
            const name = r.biller || "Unknown";
            const row = map.get(name) || { biller: name, worked: new Set(), resubmitted: new Set(), inProgress: new Set(), last: null };
            row.worked.add(r.claimId);
            if (/resubmit/i.test(r.action)) row.resubmitted.add(r.claimId);
            else if (/(rework|correct|edit|update)/i.test(r.action)) row.inProgress.add(r.claimId);
            const t = new Date(r.when);
            if (!Number.isNaN(t.getTime()) && (!row.last || t > row.last)) row.last = t;
            map.set(name, row);
        });
        const list = Array.from(map.values())
            .map((r) => ({
                biller: r.biller,
                worked: r.worked.size,
                resubmitted: r.resubmitted.size,
                inProgress: r.inProgress.size,
                last: r.last,
            }))
            .sort((a, b) => b.worked - a.worked);
        const team = list.reduce(
            (acc, r) => ({
                worked: acc.worked + r.worked,
                resubmitted: acc.resubmitted + r.resubmitted,
                inProgress: acc.inProgress + r.inProgress,
            }),
            { worked: 0, resubmitted: 0, inProgress: 0 }
        );
        return { list, team, max: list[0]?.worked || 1 };
    }, [activity]);

    // "Where those claims are now" — one bucket per claim touched today.
    const outcomeBars = useMemo(() => {
        const byClaim = new Map();
        activity.forEach((r) => {
            const cur = byClaim.get(r.claimId) || { paid: false, resubmitted: false, accepted: false, correcting: false };
            if (isPaymentEvent(r)) cur.paid = true;
            else if (/accept|clearing/i.test(`${r.action} ${r.detail}`)) cur.accepted = true;
            else if (/resubmit/i.test(r.action)) cur.resubmitted = true;
            else cur.correcting = true;
            byClaim.set(r.claimId, cur);
        });
        let paid = 0, awaiting = 0, accepted = 0, correcting = 0;
        byClaim.forEach((v) => {
            if (v.paid) paid += 1;
            else if (v.accepted) accepted += 1;
            else if (v.resubmitted) awaiting += 1;
            else correcting += 1;
        });
        const total = byClaim.size || 1;
        return {
            total: byClaim.size,
            paid,
            rows: [
                { label: "Paid ✓", count: paid, color: "#15803d" },
                { label: "Awaiting payer", count: awaiting, color: "#7c3aed" },
                { label: "Accepted by clearinghouse", count: accepted, color: "#7c3aed" },
                { label: "Still being corrected", count: correcting, color: "#dc2626" },
            ].map((r) => ({ ...r, percent: Math.round((r.count / total) * 100) })),
        };
    }, [activity]);

    const dailyKpis = [
        { label: "Claims Worked", value: new Set(activity.map((r) => r.claimId)).size, tone: KPI_TONES.tealDark },
        { label: "Resubmitted", value: billerRollup.team.resubmitted, tone: KPI_TONES.purple },
        { label: "In Progress", value: billerRollup.team.inProgress, tone: KPI_TONES.amber },
        { label: "Paid ✓", value: outcomeBars.paid, tone: KPI_TONES.green },
        { label: "Active Billers", value: billerRollup.list.length, tone: KPI_TONES.red },
    ];

    const setDailyPreset = (preset) => {
        setDailyRange(preset);
        if (preset === "today") setDailyDate(new Date().toISOString().split("T")[0]);
        if (preset === "yesterday") setDailyDate(new Date(Date.now() - DAY_MS).toISOString().split("T")[0]);
    };

    // Only open claims can still be acted on, and dueState is already null for
    // the rest — so these counts are inherently "open claims near/past deadline".
    const showAlert = !alertDismissed && !loading && view !== "daily" && (overdueCount > 0 || dueSoonCount > 0);
    const alertIsDanger = overdueCount > 0;

    // Jump from the banner straight into the work that matters.
    const reviewDueSoon = () => {
        setView("queue");
        setDueSoonOnly(true);
        setStatusChip("Needs Action");
        setBucketFilter(null);
        requestAnimationFrame(() => {
            document.getElementById("dm-queue-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    };

    const kpis = [
        { label: "Total Denied", value: groupedClaims.length, tone: KPI_TONES.red, hint: `${filtered.length} match current filters` },
        { label: "Needs Action", value: needsActionCount, tone: KPI_TONES.amber, hint: "Not yet reworked" },
        { label: "Due Soon / Overdue", value: dueSoonCount + overdueCount, tone: KPI_TONES.deep, hint: `${overdueCount} past the ${FILING_DUE_DAYS}-day window` },
        { label: "Recovered — Paid ✓", value: paidCount, tone: KPI_TONES.green, hint: "reworked & paid by payer" },
        { label: "Avg Age of Denial", value: `${avgAgeDays} days`, tone: KPI_TONES.tealDark, hint: "measured from date of service" },
    ];

    return (
        <div className="dm-shell">
            <Navbar
                handleLogout={handleLogout}
                toggleSidebar={toggleSidebar}
                isSidebarOpen={isSidebarOpen}
            />

            {/* Same structure App uses on every other page: a .hv-shell row
                holding the rail and the content beside it. That is what makes
                the sidebar PUSH the page open rather than float over it - the
                design system sizes .hv-sidebar from the `is-open` class on this
                element. The scrim only shows in the system's own overlay mode,
                below 1024px, where there is no room to push. */}
            <div className={`hv-shell dm-row${isSidebarOpen ? " is-open" : ""}`}>
                <Sidebar
                    isSidebarOpen={isSidebarOpen}
                    toggleSidebar={toggleSidebar}
                    closeSidebar={closeSidebar}
                />
                {isSidebarOpen && (
                    <div className="hv-sidebar-scrim" onClick={closeSidebar} />
                )}

                <div className="hv-shell__main dm-main">

                <div className="dm-scroll">
                    <div className="dm-page">
                        {/* Banner sits above the title so the deadline is the first
                            thing read on the page. Dismissal is session-only. */}
                        {showAlert && (
                            <div className={`dm-alert ${alertIsDanger ? "dm-alert--danger" : ""}`}>
                                <div className="dm-alert__icon">!</div>
                                <div className="dm-alert__text">
                                    {alertIsDanger ? (
                                        <>
                                            <b>{overdueCount}</b> claim{overdueCount === 1 ? " is" : "s are"} past the payer filing deadline
                                            {dueSoonCount > 0 ? <> and <b>{dueSoonCount}</b> more close within {DUE_SOON_DAYS} days</> : ""}. Resubmit before the window closes.
                                        </>
                                    ) : (
                                        <>
                                            <b>{dueSoonCount}</b> claim{dueSoonCount === 1 ? "" : "s"} reach the payer filing deadline within {DUE_SOON_DAYS} days.
                                            Resubmit before the window closes.
                                        </>
                                    )}
                                </div>
                                <button className="dm-alert__btn" onClick={reviewDueSoon}>Review now</button>
                                <button className="dm-alert__close" onClick={() => setAlertDismissed(true)} aria-label="Dismiss">✕</button>
                            </div>
                        )}

                        <div className="dm-header">
                            <div>
                                <div className="dm-header__title">Denial Management</div>
                                <div className="dm-header__sub">
                                    {loading
                                        ? "Loading denial data…"
                                        : `${filtered.length} of ${groupedClaims.length} denials · ${filterType} ${toApiDate(fromDateInput)} – ${toApiDate(toDateInput)}`}
                                </div>
                            </div>
                            <div className="dm-seg">
                                {[
                                    { key: "manage", label: "Management View" },
                                    { key: "queue", label: "Billing Work Queue" },
                                    { key: "daily", label: "Daily Activity" },
                                ].map((v) => (
                                    <button
                                        key={v.key}
                                        className={`dm-seg__btn ${view === v.key ? "dm-seg__btn--on" : ""}`}
                                        onClick={() => setView(v.key)}
                                    >
                                        {v.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {error && (
                            <div className="dm-alert dm-alert--danger">
                                <div className="dm-alert__icon">!</div>
                                <div className="dm-alert__text">{error}</div>
                                <button className="dm-alert__btn" onClick={handleApplyDates}>Retry</button>
                            </div>
                        )}

                        <div className="dm-toolbar" style={{ display: view === "daily" ? "none" : undefined }}>
                            {/* Feeds sFilterType in the API sInputJSON. */}
                            <DmSelect
                                value={filterType}
                                options={FILTER_TYPE_OPTIONS}
                                onChange={setFilterType}
                                ariaLabel="Filter by date type"
                            />

                            <label>
                                From
                                <input
                                    type="date"
                                    value={fromDateInput}
                                    min={minIso}
                                    max={toDateInput}
                                    onChange={(e) => setFromDateInput(e.target.value)}
                                />
                            </label>

                            <label>
                                To
                                <input
                                    type="date"
                                    value={toDateInput}
                                    min={fromDateInput}
                                    max={todayIso}
                                    onChange={(e) => setToDateInput(e.target.value)}
                                />
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    checked={showPracticeClaims}
                                    onChange={(e) => {
                                        // Scope change needs a server round-trip — refetch rather
                                        // than leave stale rows under a flipped checkbox.
                                        const next = e.target.checked;
                                        setShowPracticeClaims(next);
                                        fetchDeniedClaims({
                                            from: toApiDate(fromDateInput),
                                            to: toApiDate(toDateInput),
                                            fType: filterType,
                                            showPractice: next,
                                        });
                                    }}
                                />
                                Show practice claims
                            </label>

                            <button className="dm-btn" onClick={handleApplyDates}>Search</button>

                            <div className="dm-quick">
                                {[
                                    { key: "week", label: "1 Week" },
                                    { key: "15days", label: "15 Days" },
                                    { key: "month", label: "1 Month" },
                                ].map(({ key, label }) => (
                                    <button
                                        key={key}
                                        className={`dm-quick__btn ${dateTab === key ? "dm-quick__btn--on" : ""}`}
                                        onClick={() => handleTabChange(key)}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>

                            <div className="dm-toolbar__search">
                                <input
                                    placeholder="Search anything…"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Escape") setSearchText(""); }}
                                    aria-label="Search all columns"
                                />
                            </div>

                            <button className="dm-wq__export" onClick={exportQueue}>
                                Export{selected.size > 0 ? ` (${selected.size})` : ""}
                            </button>
                        </div>

                        {/* Denial KPIs and aging buckets belong to the denial views;
                            Daily Activity brings its own count cards. */}
                        {view !== "daily" && (
                            <>
                                <div className="dm-kpis">
                                    {kpis.map((kpi) => (
                                        <div key={kpi.label} className="dm-kpi" style={{ borderLeftColor: kpi.tone }}>
                                            <div className="dm-kpi__value" style={{ color: kpi.tone }}>{loading ? "–" : kpi.value}</div>
                                            <div className="dm-kpi__label">{kpi.label}</div>
                                            <div className="dm-kpi__hint" style={{ color: kpi.tone }}>{loading ? "" : kpi.hint}</div>
                                        </div>
                                    ))}
                                </div>

                                <div className="dm-buckets__title">
                                    Denial Aging — click a bucket to filter{bucketFilter ? " (click again to clear)" : ""}
                                </div>
                                <div className="dm-buckets">
                                    {AGING_BUCKETS.map((bucket) => {
                                        const count = bucketCounts[bucket.id];
                                        const pct = groupedClaims.length ? Math.round((count / groupedClaims.length) * 100) : 0;
                                        return (
                                            <button
                                                key={bucket.id}
                                                type="button"
                                                aria-pressed={bucketFilter === bucket.id}
                                                className={`dm-bucket dm-bucket--age-${bucket.id} ${bucketFilter === bucket.id ? "dm-bucket--sel" : ""}`}
                                                onClick={() => handleBucketClick(bucket.id)}
                                            >
                                                <div className="dm-bucket__range">{bucket.range}</div>
                                                <div className="dm-bucket__count">{loading ? "–" : count}</div>
                                                <div className="dm-bucket__meter"><i style={{ width: `${pct}%` }} /></div>
                                                <div className="dm-bucket__hint">{pct}% of denials · {bucket.hint}</div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {view === "manage" && (
                            <>
                                <div className="dm-grid2">
                                    <div className="dm-panel">
                                        <div className="dm-panel__title">Top denial reasons</div>
                                        {!loading && reasonBreakdown.length === 0 && <div className="dm-empty">No records found</div>}
                                        {reasonBreakdown.map((entry) => (
                                            <div key={entry.code} className="dm-bar">
                                                <div className="dm-bar__top">
                                                    <b>{entry.code}</b>
                                                    <span>{entry.count} claims · {entry.percent}%</span>
                                                </div>
                                                <div className="dm-bar__track">
                                                    <i className="dm-bar__fill" style={{ width: `${Math.max(entry.percent, 2)}%` }} />
                                                </div>
                                                {entry.why && <div className="dm-bar__why">{entry.why}</div>}
                                            </div>
                                        ))}
                                    </div>

                                    <div className="dm-panel">
                                        <div className="dm-panel__title">Denials by payer</div>
                                        {!loading && payerBreakdown.length === 0 && <div className="dm-empty">No records found</div>}
                                        {payerBreakdown.map((entry) => (
                                            <div key={entry.payer} className="dm-bar">
                                                <div className="dm-bar__top">
                                                    <b>{entry.payer}</b>
                                                    <span>{entry.count} claims · {entry.percent}%</span>
                                                </div>
                                                <div className="dm-bar__track">
                                                    <i className="dm-bar__fill" style={{ width: `${Math.max(entry.percent, 2)}%` }} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="dm-panel">
                                    <div className="dm-panel__title">Payer × aging — claim counts</div>
                                    <div style={{ overflowX: "auto" }}>
                                        <table className="dm-matrix">
                                            <thead>
                                                <tr>
                                                    <th>Payer</th>
                                                    {AGING_BUCKETS.map((b) => <th key={b.id}>{b.range}</th>)}
                                                    <th>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {payerMatrix.list.map((row) => (
                                                    <tr key={row.payer}>
                                                        <td>{row.payer}</td>
                                                        {AGING_BUCKETS.map((b) => (
                                                            <td key={b.id} className={b.id >= 4 && row.cells[b.id] > 0 ? "is-hot" : ""}>
                                                                {row.cells[b.id] || "–"}
                                                            </td>
                                                        ))}
                                                        <td>{row.total}</td>
                                                    </tr>
                                                ))}
                                                {payerMatrix.list.length > 0 && (
                                                    <tr className="is-total">
                                                        <td>{payerMatrix.totals.payer}</td>
                                                        {AGING_BUCKETS.map((b) => <td key={b.id}>{payerMatrix.totals.cells[b.id] || "–"}</td>)}
                                                        <td>{payerMatrix.totals.total}</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    {!loading && payerMatrix.list.length === 0 && <div className="dm-empty">No records found</div>}
                                    {payerMatrix.list.length > 0 && (
                                        <div className="dm-bar__why" style={{ marginTop: 10 }}>
                                            Showing the top {payerMatrix.list.length} payers by denial count.
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        {view === "daily" && (
                            <>
                                <div className="dm-daily__bar">
                                    <label>
                                        Date
                                        <input
                                            type="date"
                                            value={dailyDate}
                                            max={todayIso}
                                            onChange={(e) => { setDailyDate(e.target.value); setDailyRange("custom"); }}
                                        />
                                    </label>
                                    <div className="dm-quick">
                                        {[
                                            { key: "today", label: "Today" },
                                            { key: "yesterday", label: "Yesterday" },
                                            { key: "week", label: "This Week" },
                                        ].map((p) => (
                                            <button
                                                key={p.key}
                                                className={`dm-quick__btn ${dailyRange === p.key ? "dm-quick__btn--on" : ""}`}
                                                onClick={() => setDailyPreset(p.key)}
                                            >
                                                {p.label}
                                            </button>
                                        ))}
                                    </div>
                                    <span className="dm-wq__spacer" />
                                    <button className="dm-wq__export" onClick={fetchDailyActivity}>Refresh</button>
                                </div>

                                <div className="dm-kpis">
                                    {dailyKpis.map((kpi) => (
                                        <div key={kpi.label} className="dm-kpi" style={{ borderLeftColor: kpi.tone }}>
                                            <div className="dm-kpi__value" style={{ color: kpi.tone }}>{dailyLoading ? "–" : kpi.value}</div>
                                            <div className="dm-kpi__label">{kpi.label}</div>
                                        </div>
                                    ))}
                                </div>

                                {dailyError && !dailyLoading && (
                                    <div className="dm-alert">
                                        <div className="dm-alert__icon">!</div>
                                        <div className="dm-alert__text">{dailyError}</div>
                                        <button className="dm-alert__btn" onClick={fetchDailyActivity}>Retry</button>
                                    </div>
                                )}

                                <div className="dm-grid2">
                                    <div className="dm-panel">
                                        <div className="dm-panel__title">Worked by Biller</div>
                                        {dailyLoading && <div className="dm-empty">Loading activity…</div>}
                                        {!dailyLoading && billerRollup.list.length === 0 && (
                                            <div className="dm-empty">No denial activity for this date</div>
                                        )}
                                        {!dailyLoading && billerRollup.list.length > 0 && (
                                            <table className="dm-matrix">
                                                <thead>
                                                    <tr>
                                                        <th>Biller</th>
                                                        <th>Worked</th>
                                                        <th>Resubmitted</th>
                                                        <th>In Progress</th>
                                                        <th>Last Activity</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {billerRollup.list.map((r) => (
                                                        <tr key={r.biller}>
                                                            <td>
                                                                <div>{r.biller}</div>
                                                                {/* Ranking bar — relative to the busiest biller */}
                                                                <div className="dm-rank">
                                                                    <i style={{ width: `${Math.round((r.worked / billerRollup.max) * 100)}%` }} />
                                                                </div>
                                                            </td>
                                                            <td>{r.worked}</td>
                                                            <td>{r.resubmitted}</td>
                                                            <td>{r.inProgress}</td>
                                                            <td>{r.last ? r.last.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "–"}</td>
                                                        </tr>
                                                    ))}
                                                    <tr className="is-total">
                                                        <td>Team total</td>
                                                        <td>{billerRollup.team.worked}</td>
                                                        <td>{billerRollup.team.resubmitted}</td>
                                                        <td>{billerRollup.team.inProgress}</td>
                                                        <td>–</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        )}
                                    </div>

                                    <div className="dm-panel">
                                        <div className="dm-panel__title">Where those claims are now</div>
                                        {!dailyLoading && outcomeBars.total === 0 && <div className="dm-empty">Nothing worked yet</div>}
                                        {outcomeBars.rows.map((r) => (
                                            <div key={r.label} className="dm-bar">
                                                <div className="dm-bar__top">
                                                    <b>{r.label}</b>
                                                    <span>{r.count} claims · {r.percent}%</span>
                                                </div>
                                                <div className="dm-bar__track">
                                                    <i className="dm-bar__fill" style={{ width: `${Math.max(r.percent, 2)}%`, background: r.color }} />
                                                </div>
                                            </div>
                                        ))}
                                        {outcomeBars.total > 0 && (
                                            <div className="dm-goodnews">
                                                <b>{outcomeBars.paid}</b> of <b>{outcomeBars.total}</b> claims worked in this window have already been paid by the payer.
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="dm-panel">
                                    <div className="dm-panel__title">Activity Feed</div>
                                    {dailyLoading && <div className="dm-empty">Loading activity…</div>}
                                    {!dailyLoading && activity.length === 0 && (
                                        <div className="dm-empty">No denial activity for this date</div>
                                    )}
                                    {!dailyLoading && activity.length > 0 && (
                                        <div className="dm-tl">
                                            {activity.slice(0, 60).map((r, i) => (
                                                <div key={`${r.claimId}-${i}`} className={`dm-tl__item ${isPaymentEvent(r) ? "dm-tl__item--paid" : ""}`}>
                                                    {isPaymentEvent(r) ? (
                                                        <b>835 remit posted — payment received on CLM-{r.claimId} ✓ PAID</b>
                                                    ) : (
                                                        <b>
                                                            {r.biller || "System"} {r.action || "worked"}, resubmitted CLM-{r.claimId}
                                                            {r.detail ? ` · was ${r.detail}` : ""}
                                                        </b>
                                                    )}
                                                    <span>
                                                        {r.when ? new Date(r.when).toLocaleString("en-US", {
                                                            month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                                                        }) : "–"}
                                                    </span>
                                                </div>
                                            ))}
                                            {activity.length > 60 && (
                                                <div className="dm-bar__why">Showing the 60 most recent of {activity.length} events.</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                        <div id="dm-queue-anchor" />
                        {view === "queue" && (
                            <div className="dm-wq">
                                {/* 1 ── Toolbar. Global search and Export live in the
                                    page filter bar — a second copy here drove the same
                                    searchText state from two inputs. */}
                                <div className="dm-wq__toolbar">
                                    <span className="dm-wq__title">Denial Work Queue</span>
                                    <span className="dm-wq__count">({sorted.length} claim{sorted.length === 1 ? "" : "s"})</span>
                                </div>

                                {/* Status tabs — a claim sits in exactly one, so the three
                                    non-"All" badges always sum to the "All" badge. */}
                                <div className="dm-tabs">
                                    <div className="dm-tabs__list" role="tablist">
                                        {STATUS_TABS.map((tab) => (
                                            <button
                                                key={tab.key}
                                                role="tab"
                                                aria-selected={statusChip === tab.key}
                                                className={`dm-tab ${statusChip === tab.key ? "dm-tab--on" : ""} ${tab.tone ? `dm-tab--${tab.tone}` : ""}`}
                                                onClick={() => setStatusChip(tab.key)}
                                            >
                                                {tab.label}
                                                <span className="dm-tab__badge">{tabCounts[tab.key] ?? 0}</span>
                                            </button>
                                        ))}
                                    </div>
                                    <button
                                        className={`dm-duechip ${dueSoonOnly ? "dm-duechip--on" : ""}`}
                                        aria-pressed={dueSoonOnly}
                                        onClick={() => setDueSoonOnly((v) => !v)}
                                        title={`Only claims within ${DUE_SOON_DAYS} days of the filing deadline, or past it`}
                                    >
                                        ⚠ Due Soon
                                    </button>
                                </div>

                                {/* 2 ── Pagination, at the top */}
                                <div className="dm-wq__pager">
                                    <span>
                                        {sorted.length === 0
                                            ? "Showing 0 of 0"
                                            : `Showing ${(currentPage - 1) * effectivePageSize + 1}–${Math.min(currentPage * effectivePageSize, sorted.length)} of ${sorted.length}`}
                                    </span>
                                    <div className="dm-wq__pagectl">
                                        <label className="dm-wq__rows">
                                            Rows per page
                                            <select
                                                value={pageSize}
                                                onChange={(e) => setPageSize(Number(e.target.value))}
                                                aria-label="Rows per page"
                                            >
                                                {PAGE_SIZES.map((size) => (
                                                    <option key={size} value={size}>{size === ALL_ROWS ? "All" : size}</option>
                                                ))}
                                            </select>
                                        </label>
                                        {totalPages > 1 && (
                                            <>
                                                <button
                                                    className="dm-pg__btn"
                                                    disabled={currentPage === 1}
                                                    onClick={() => setPage(currentPage - 1)}
                                                    aria-label="Previous page"
                                                >
                                                    ‹
                                                </button>
                                                {pageWindow.map((p) => (
                                                    <button
                                                        key={p}
                                                        className={`dm-pg__btn ${p === currentPage ? "dm-pg__btn--on" : ""}`}
                                                        onClick={() => setPage(p)}
                                                        aria-current={p === currentPage ? "page" : undefined}
                                                    >
                                                        {p}
                                                    </button>
                                                ))}
                                                <button
                                                    className="dm-pg__btn"
                                                    disabled={currentPage === totalPages}
                                                    onClick={() => setPage(currentPage + 1)}
                                                    aria-label="Next page"
                                                >
                                                    ›
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* 3 ── Active filter chips */}
                                {activeFilters.length > 0 && (
                                    <div className="dm-wq__filters">
                                        {activeFilters.map((f) => (
                                            <span key={f.key} className="dm-fchip">
                                                <span><b>{f.label}:</b> {f.value}</span>
                                                <button
                                                    className="dm-fchip__x"
                                                    onClick={() => handleColumnSearch(f.key, "")}
                                                    aria-label={`Remove ${f.label} filter`}
                                                >
                                                    ✕
                                                </button>
                                            </span>
                                        ))}
                                        <button className="dm-wq__clear" onClick={() => setColumnSearch(emptyColumnSearch())}>
                                            Clear all
                                        </button>
                                    </div>
                                )}

                                <div className="dm-wq__scroll">
                                    <table className="dm-grid">
                                        <thead>
                                            {/* 4 ── Sortable header labels */}
                                            <tr>
                                                <th className="dm-grid__check">
                                                    <input
                                                        type="checkbox"
                                                        checked={allFilteredSelected}
                                                        onChange={toggleSelectAllFiltered}
                                                        aria-label="Select all filtered claims"
                                                    />
                                                </th>
                                                <th style={{ width: 50 }}>S.No</th>
                                                {COLUMNS.map((col) => (
                                                    <th
                                                        key={col.key}
                                                        className={`dm-grid__sortable ${sortKey === col.key ? "is-sorted" : ""}`}
                                                        style={col.width ? { width: col.width } : undefined}
                                                        onClick={() => handleSort(col.key)}
                                                    >
                                                        {col.label}
                                                        <span className="dm-grid__sort">
                                                            {sortKey === col.key ? (sortDir === "asc" ? "▲" : "▼") : ""}
                                                        </span>
                                                    </th>
                                                ))}
                                                <th style={{ width: 100 }}>Action</th>
                                            </tr>

                                            {/* 5 ── Always-visible column search */}
                                            <tr className="dm-grid__filterrow">
                                                <th />
                                                <th />
                                                {COLUMNS.map((col) => (
                                                    <th key={col.key}>
                                                        {col.search !== false && (
                                                            <input
                                                                className="dm-grid__csearch"
                                                                value={columnSearch[col.key]}
                                                                onChange={(e) => handleColumnSearch(col.key, e.target.value)}
                                                                onKeyDown={(e) => { if (e.key === "Escape") handleColumnSearch(col.key, ""); }}
                                                                placeholder="Search"
                                                                autoComplete="off"
                                                                aria-label={`Search ${col.label}`}
                                                            />
                                                        )}
                                                    </th>
                                                ))}
                                                <th />
                                            </tr>
                                        </thead>

                                        {/* 6 ── Body */}
                                        <tbody>
                                            {paged.map((item, idx) => (
                                                <tr key={item._key} onClick={() => openDrawer(item)}>
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selected.has(item._key)}
                                                            onChange={() => toggleSelect(item._key)}
                                                            aria-label={`Select claim ${item.ClaimID}`}
                                                        />
                                                    </td>
                                                    <td className="dm-grid__num">{(currentPage - 1) * effectivePageSize + idx + 1}</td>
                                                    <td>
                                                        {item.ClaimID ? (
                                                            <a
                                                                className="dm-grid__claim"
                                                                href={`/#/new-encounter-v2/${item.ClaimID}`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                onClick={(e) => e.stopPropagation()}
                                                                title="Open this claim in the encounter page"
                                                            >
                                                                CLM-{item.ClaimID}
                                                            </a>
                                                        ) : (
                                                            <span style={{ color: "#94a3b8", fontWeight: 700 }}>No claim ID</span>
                                                        )}
                                                    </td>
                                                    <td className="dm-grid__patient">{item._patient}</td>
                                                    <td>{formatDOSDate(item.DOS)}</td>
                                                    <td>
                                                        <span className={`dm-age dm-age--${item._bucket || 1}`}>
                                                            {item._ageDays === null ? "–" : `${item._ageDays}d`} · {bucketShort(item._bucket)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {item._dueState === "overdue" || item._dueState === "duesoon" ? (
                                                            <span className={`dm-due dm-due--${item._dueState}`}>{filingCellText(item)}</span>
                                                        ) : (
                                                            <span className="dm-due dm-due--none">{filingCellText(item)}</span>
                                                        )}
                                                    </td>
                                                    <td>{item._payer}</td>
                                                    <td>{item.CPT || "–"}</td>
                                                    <td className="dm-grid__code">{item.DeniedCode}</td>
                                                    <td className="dm-grid__reason" title={item.ErrorDetails}>{item.ErrorDetails || "–"}</td>
                                                    <td>{item._submittedBy || "–"}</td>
                                                    <td><span className={statusPillClass(item)}>{statusLabel(item)}</span></td>
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        <button
                                                            className={`dm-rowbtn ${item._isPaid ? "dm-rowbtn--view" : ""}`}
                                                            onClick={() => openDrawer(item)}
                                                            title={item._isPaid
                                                                ? `View CLM-${item.ClaimID} — payment already posted`
                                                                : `Open the rework drawer for CLM-${item.ClaimID}`}
                                                        >
                                                            {item._isPaid ? "View" : "Rework"}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {loading && <div className="dm-wq__empty">Loading denial data…</div>}
                                {!loading && paged.length === 0 && (
                                    <div className="dm-wq__empty">No denials match your filters</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                </div>
            </div>

            <div className={`dm-overlay ${drawerClaim ? "dm-overlay--on" : ""}`} onClick={closeDrawer} />

            <aside className={`dm-drawer ${drawerClaim ? "dm-drawer--on" : ""}`}>
                {drawerClaim && (
                    <>
                        <div className="dm-drawer__head">
                            <div className="dm-drawer__claim">CLM-{drawerClaim.ClaimID}</div>
                            <div className="dm-drawer__sub">
                                {drawerClaim._patient} · {drawerClaim._payer} · DOS {formatDOSDate(drawerClaim.DOS)}
                            </div>
                        </div>

                        <div className="dm-drawer__body">
                            <div className="dm-drawer__sec">
                                <h4>Claim</h4>
                                <div className="dm-kv">
                                    <div className="k">Status</div>
                                    <div className="v"><span className={statusPillClass(drawerClaim)}>{statusLabel(drawerClaim)}</span></div>
                                    <div className="k">Age</div>
                                    <div className="v">{drawerClaim._ageDays === null ? "–" : `${drawerClaim._ageDays} days`}</div>
                                    <div className="k">CPT</div>
                                    <div className="v">{drawerClaim.CPT || "–"}</div>
                                    <div className="k">Billed</div>
                                    <div className="v">${parseAmount(drawerClaim.TotalCharge).toFixed(2)}</div>
                                    <div className="k">Location</div>
                                    <div className="v">{drawerClaim.AppLocationName || "–"}</div>
                                    <div className="k">Provider</div>
                                    <div className="v">{drawerClaim.ProvderName || "–"}</div>
                                </div>
                            </div>

                            <div className="dm-drawer__sec">
                                <h4>Denial</h4>
                                <div className="dm-callout">
                                    <b>{drawerClaim.DeniedCode}</b>
                                    {drawerClaim.ErrorDetails ? ` — ${drawerClaim.ErrorDetails}` : " — no reason text returned by the payer."}
                                </div>
                                {(drawerClaim._dueState === "overdue" || drawerClaim._dueState === "duesoon") && (
                                    <div className="dm-callout dm-callout--due">
                                        {drawerClaim._dueState === "overdue"
                                            ? `Past the ${FILING_DUE_DAYS}-day payer filing window by ${Math.abs(drawerClaim._daysToDue)} days — likely unrecoverable.`
                                            : `Filing window closes in ${drawerClaim._daysToDue} days — resubmit before it shuts.`}
                                    </div>
                                )}
                            </div>

                            <div className="dm-drawer__sec">
                                <h4>Rework &amp; resubmit</h4>
                                <div className="dm-rework">
                                    <div className="dm-rework__grid">
                                        <div className="dm-rework__field">
                                            <label htmlFor="dm-cpt">CPT</label>
                                            <input id="dm-cpt" value={resubCPT} onChange={(e) => setResubCPT(e.target.value)} />
                                        </div>
                                        <div className="dm-rework__field">
                                            <label htmlFor="dm-icd">ICD-10</label>
                                            <input id="dm-icd" value={resubICD} onChange={(e) => setResubICD(e.target.value)} />
                                        </div>
                                        <div className="dm-rework__field">
                                            <label htmlFor="dm-dos">DOS</label>
                                            <input id="dm-dos" type="date" value={resubDOS} onChange={(e) => setResubDOS(e.target.value)} />
                                        </div>
                                    </div>
                                    <textarea
                                        rows={3}
                                        value={resubNotes}
                                        onChange={(e) => setResubNotes(e.target.value)}
                                        placeholder="Resubmission notes…"
                                    />
                                    <div className="dm-rework__actions">
                                        <button onClick={handleResubmitSubmit} disabled={resubLoading}>
                                            {resubLoading ? "Submitting…" : "Save & resubmit"}
                                        </button>
                                    </div>
                                    {resubMsg && (
                                        <div className={`dm-rework__ok dm-rework__ok--on ${resubMsg.ok ? "" : "dm-rework__ok--err"}`}>
                                            {resubMsg.text}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="dm-drawer__sec">
                                <h4>Timeline</h4>
                                <div className="dm-tl">
                                    <div className="dm-tl__item">
                                        <b>Date of service</b>
                                        <span>{formatDOSDate(drawerClaim.DOS) || "–"}</span>
                                    </div>
                                    {drawerClaim.ClaimSubmitDate && (
                                        <div className="dm-tl__item">
                                            <b>Claim submitted</b>
                                            <span>{formatDOSDate(drawerClaim.ClaimSubmitDate)}{drawerClaim.SubmittedUserName ? ` · ${drawerClaim.SubmittedUserName}` : ""}</span>
                                        </div>
                                    )}
                                    <div className="dm-tl__item">
                                        <b>Denied — {drawerClaim.DeniedCode}</b>
                                        <span>{drawerClaim._ageDays === null ? "–" : `${drawerClaim._ageDays} days since DOS`}</span>
                                    </div>
                                    {drawerClaim.ClaimStatus !== "Denied" && (
                                        <div className="dm-tl__item">
                                            <b>{drawerClaim.ClaimStatus}</b>
                                            <span>{drawerClaim.UpdatedUserName ? `by ${drawerClaim.UpdatedUserName}` : "status updated"}</span>
                                        </div>
                                    )}
                                    <div className="dm-tl__item">
                                        <b>Timely filing</b>
                                        <span>{dueLabelFor(drawerClaim._daysToDue)} of the {FILING_DUE_DAYS}-day window from submit date</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="dm-drawer__foot">
                            <button style={{ background: "#d97706" }} onClick={() => openEncounter(drawerClaim)}>
                                Correct &amp; Resubmit
                            </button>
                            <button style={{ background: "#64748b", flex: "0 0 96px" }} onClick={closeDrawer}>Close</button>
                        </div>
                    </>
                )}
            </aside>
        </div>
    );
}
